import { EventContextProvider } from "@codat/analytics";
import { Modal, useToast } from "@codat/orchard-ui";
import React, { useEffect, useState } from "react";

import CodeBlock from "components/CodeBlock";

import { useProxyApi } from "../../../contexts/ProxyApiContext";

export interface ValidationErrorModalProps {
  companyId: string;
  datasetId?: string;
  datatype?: string;
  requestedAt?: string;
  validationErrorUrl?: string;
  onClose: () => void;
}

const ValidationErrorModal: React.FC<ValidationErrorModalProps> = ({
  companyId,
  datasetId,
  datatype,
  requestedAt,
  validationErrorUrl,
  onClose,
}) => {
  const { addToast } = useToast();
  const proxyApi = useProxyApi();

  const [isLoading, setIsLoading] = useState(true);
  const [validationErrorData, setValidationErrorData] = useState();

  useEffect(() => {
    setIsLoading(true);

    if (validationErrorUrl) {
      proxyApi
        .get(validationErrorUrl)
        .then((res) => {
          setValidationErrorData(res.data);
        })
        .catch(() => {
          addToast({
            variant: "error",
            message: "Error getting validation error details.",
          });
        })
        .finally(() => setIsLoading(false));
    }
  }, [validationErrorUrl, addToast, proxyApi]);

  return (
    <EventContextProvider name="Validation error modal">
      <Modal onClose={onClose} size="medium">
        <>
          <Modal.Header title="Validation error response" onClose={onClose} />
          <Modal.Body data-testid="validation-error-modal" variant="light">
            <Modal.Section variant="fill">
              <CodeBlock
                codeBlockName="Validation response"
                eventProperties={{
                  companyId,
                  datasetId,
                  dataType: datatype,
                }}
                isSkeleton={isLoading}
                data-testid="validation-error-modal-code-block"
                code={validationErrorData}
                copyOptions={{
                  onSuccess: () => {
                    addToast({
                      message: "Copied validation response to clipboard",
                      variant: "success",
                    });
                  },
                  onFailure: (err) => {
                    addToast({
                      variant: "error",
                      message: "Error copying validation response to clipboard",
                    });
                    //eslint-disable-next-line no-console
                    console.error(err);
                  },
                }}
                downloadOptions={{
                  filename: `${companyId} - ${datasetId}.${datatype} - ${requestedAt}.json`,
                  onFailure: (err) => {
                    addToast({
                      variant: "error",
                      message: "Error downloading validation response",
                    });
                    //eslint-disable-next-line no-console
                    console.error(err);
                  },
                }}
              />
            </Modal.Section>
          </Modal.Body>
        </>
      </Modal>
    </EventContextProvider>
  );
};

export default ValidationErrorModal;
